import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/apiClient';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, UserCheck, Search, Filter, X, UserMinus, Trash2 } from 'lucide-react';

const WardenStudentAssignment = () => {
  const { wardenId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [warden, setWarden] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);

  // Filter states for available students
  const [availableFilters, setAvailableFilters] = useState({
    search: '',
    class: '',
    minAge: '',
    maxAge: '',
    showUnassignedOnly: false
  });

  // Search for already assigned students
  const [assignedSearch, setAssignedSearch] = useState('');

  const [classes, setClasses] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, [wardenId]);

  useEffect(() => {
    filterStudents();
  }, [allStudents, availableFilters, assignedSearch]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch warden details
      const wardenRes = await apiClient.get(`/v1/admin/wardens/${wardenId}`);
      setWarden(wardenRes.data);

      // Fetch all students
      const studentsRes = await apiClient.get('/v1/admin/students/', {
        params: { hostel_id: user.hostel_id }
      });
      const studentsData = Array.isArray(studentsRes.data) ? studentsRes.data : [];
      setAllStudents(studentsData);

      // Extract unique classes
      const uniqueClasses = [...new Set(studentsData.map(s => s.class_name).filter(Boolean))];
      setClasses(uniqueClasses.sort());
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/admin/wardens');
    } finally {
      setLoading(false);
    }
  };

  const filterStudents = () => {
    // Separate assigned and available students
    const isWardenAssigned = (s) =>
      s.assigned_warden_id === wardenId ||
      s.secondary_warden_id === wardenId ||
      (Array.isArray(s.assigned_warden_ids) && s.assigned_warden_ids.includes(wardenId));
    const assigned = allStudents.filter(isWardenAssigned);
    const available = allStudents.filter(s => !isWardenAssigned(s));

    // Apply search to assigned students
    let filteredAssigned = assigned;
    if (assignedSearch) {
      const searchLower = assignedSearch.toLowerCase();
      filteredAssigned = assigned.filter(s =>
        s.name.toLowerCase().includes(searchLower) ||
        (s.readable_id && s.readable_id.toLowerCase().includes(searchLower))
      );
    }
    setAssignedStudents(filteredAssigned);

    // Apply filters to available students
    let filteredAvailable = [...available];

    // Search filter
    if (availableFilters.search) {
      const searchLower = availableFilters.search.toLowerCase();
      filteredAvailable = filteredAvailable.filter(s =>
        s.name.toLowerCase().includes(searchLower) ||
        (s.readable_id && s.readable_id.toLowerCase().includes(searchLower))
      );
    }

    // Class filter
    if (availableFilters.class) {
      filteredAvailable = filteredAvailable.filter(s => s.class_name === availableFilters.class);
    }

    // Age filter
    if (availableFilters.minAge) {
      filteredAvailable = filteredAvailable.filter(s => s.age >= parseInt(availableFilters.minAge));
    }
    if (availableFilters.maxAge) {
      filteredAvailable = filteredAvailable.filter(s => s.age <= parseInt(availableFilters.maxAge));
    }

    // Unassigned only
    if (availableFilters.showUnassignedOnly) {
      filteredAvailable = filteredAvailable.filter(s =>
        (!s.assigned_warden_id || s.assigned_warden_id === '') &&
        (!s.secondary_warden_id || s.secondary_warden_id === '') &&
        (!Array.isArray(s.assigned_warden_ids) || s.assigned_warden_ids.length === 0)
      );
    }

    setAvailableStudents(filteredAvailable);
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === availableStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(availableStudents.map(s => s.id));
    }
  };

  const handleUnassignStudent = async (student) => {
    toast.error("Once a student is assigned, they cannot be directly removed. They can only be reassigned to another warden.");
  };

  const handleRemoveAll = async () => {
    toast.error("Once a student is assigned, they cannot be directly removed. They can only be reassigned to another warden.");
  };

  const handleAssign = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setAssigning(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      for (const studentId of selectedStudents) {
        await apiClient.put(`/v1/admin/students/${studentId}`, {
          assigned_warden_id: wardenId,
          assignment_date: today
        });
      }

      // Update local state
      setAllStudents(prev =>
        prev.map(s =>
          selectedStudents.includes(s.id)
            ? { ...s, assigned_warden_id: wardenId, assignment_date: today }
            : s
        )
      );

      setSelectedStudents([]);
      toast.success(`Successfully assigned ${selectedStudents.length} student(s) to ${warden.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const clearAvailableFilters = () => {
    setAvailableFilters({
      search: '',
      class: '',
      minAge: '',
      maxAge: '',
      showUnassignedOnly: false
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/wardens')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Wardens
          </Button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>
              Manage Students for Warden
            </h1>
            {warden && (
              <p className="text-gray-600 mt-1">
                {warden.name} • {warden.phone} • Currently managing {assignedStudents.length} student(s)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section 1: Already Assigned Students */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold" style={{ color: '#2C5F7F' }}>
            Already Assigned Students ({assignedStudents.length})
          </h2>
          {assignedStudents.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemoveAll}
              disabled={assigning}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove All
            </Button>
          )}
        </div>

        {/* Search for assigned students */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={assignedSearch}
              onChange={(e) => setAssignedSearch(e.target.value)}
              placeholder="Search assigned students by name or ID..."
              className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {assignedStudents.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            No students currently assigned to this warden
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hostel ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assignedStudents.map(student => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{student.name}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {student.readable_id || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {student.class_name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {student.age || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {student.room_number || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {student.hostel_id ? student.hostel_id.substring(0, 8) + '...' : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {student.assignment_date
                        ? new Date(student.assignment_date).toLocaleDateString()
                        : 'N/A'
                      }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => handleUnassignStudent(student)}
                        className="inline-flex items-center px-3 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors text-sm"
                        disabled={assigning}
                      >
                        <UserMinus className="w-3 h-3 mr-1" />
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Section 2: Available Students to Assign */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4" style={{ color: '#2C5F7F' }}>
          Assign New Students
        </h2>

        {/* Filters Section */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium" style={{ color: '#2C5F7F' }}>
              Filter Students
            </h3>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              {(availableFilters.search || availableFilters.class || availableFilters.minAge || availableFilters.maxAge || availableFilters.showUnassignedOnly) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAvailableFilters}
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={availableFilters.search}
                    onChange={(e) => setAvailableFilters({ ...availableFilters, search: e.target.value })}
                    placeholder="Name or ID"
                    className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Class */}
              <div>
                <label className="block text-sm font-medium mb-1">Class</label>
                <select
                  value={availableFilters.class}
                  onChange={(e) => setAvailableFilters({ ...availableFilters, class: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Classes</option>
                  {classes.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              {/* Age Range */}
              <div>
                <label className="block text-sm font-medium mb-1">Min Age</label>
                <input
                  type="number"
                  value={availableFilters.minAge}
                  onChange={(e) => setAvailableFilters({ ...availableFilters, minAge: e.target.value })}
                  placeholder="Min"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Max Age</label>
                <input
                  type="number"
                  value={availableFilters.maxAge}
                  onChange={(e) => setAvailableFilters({ ...availableFilters, maxAge: e.target.value })}
                  placeholder="Max"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Unassigned Only */}
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={availableFilters.showUnassignedOnly}
                    onChange={(e) => setAvailableFilters({ ...availableFilters, showUnassignedOnly: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium">Unassigned Only</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Students List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium" style={{ color: '#2C5F7F' }}>
              Students ({selectedStudents.length} of {availableStudents.length} selected)
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedStudents.length === availableStudents.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          {availableStudents.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              No students available matching the filters
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
              {availableStudents.map(student => {
                const isSelected = selectedStudents.includes(student.id);
                const hasOtherWarden = (student.assigned_warden_id && student.assigned_warden_id !== wardenId) ||
                  (student.secondary_warden_id && student.secondary_warden_id !== wardenId) ||
                  (Array.isArray(student.assigned_warden_ids) && student.assigned_warden_ids.some(id => id !== wardenId));

                return (
                  <div
                    key={student.id}
                    onClick={() => handleStudentToggle(student.id)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold">{student.name}</p>
                        <p className="text-sm text-gray-600">{student.readable_id}</p>
                        <p className="text-sm text-gray-600">
                          {student.class_name} • Age {student.age}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Room {student.room_number}
                        </p>
                        {hasOtherWarden && (
                          <p className="text-xs text-orange-600 mt-1">
                            Assigned to another warden
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/wardens')}
          disabled={assigning}
        >
          Done
        </Button>
        <Button
          onClick={handleAssign}
          disabled={assigning || selectedStudents.length === 0}
          className="btn-primary text-white"
        >
          {assigning ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Assigning...
            </>
          ) : (
            <>
              <UserCheck className="w-4 h-4 mr-2" />
              Assign {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default WardenStudentAssignment;
